import { createContext, useContext } from 'react';
import { type ProjectSummary } from '../api/client';

export type ProjectStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface ProjectContextValue {
  status: ProjectStatus;
  /** The project for the active `/projects/:id/...` route, or null off a project route. */
  currentProject: ProjectSummary | null;
  /** The error from the last failed load (only meaningful when status==='error'). */
  error: unknown;
  /** Re-fetches the active project; resolves with it, or null when there's no active id. */
  refresh: () => Promise<ProjectSummary | null>;
}

const DEFAULT_PROJECT_CONTEXT: ProjectContextValue = {
  status: 'idle',
  currentProject: null,
  error: null,
  refresh: async () => null,
};

export const ProjectContext = createContext<ProjectContextValue | null>(null);

/**
 * The active project (name, etc.) for the current project route. Unlike {@link useOrg},
 * this is tolerant of a missing provider — it returns an inert default so chrome that
 * consumes it (breadcrumbs, header) renders fine in isolation/tests without a provider.
 */
export function useProject(): ProjectContextValue {
  return useContext(ProjectContext) ?? DEFAULT_PROJECT_CONTEXT;
}
